import { formatTimestamp, type TranscriptSegment } from '@sl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { StatusChip } from '../components/StatusChip.js';
import { meetingsApi, type ClientLogRow } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const PROCESSING = new Set(['RECORDING', 'UPLOADED', 'TRANSCRIBING', 'DRAFTING']);

export function MeetingPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { canWrite, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement>(null);
  const refreshingAudio = useRef(false);

  const [activeSegment, setActiveSegment] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [speakerDialog, setSpeakerDialog] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const meetingQuery = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    enabled: Boolean(id),
  });

  const meeting = meetingQuery.data?.meeting;
  const processing = meeting ? PROCESSING.has(meeting.status) : false;

  const transcriptQuery = useQuery({
    queryKey: ['transcript', id],
    queryFn: () => meetingsApi.transcript(id),
    enabled: Boolean(id),
    refetchInterval: processing ? 8000 : false,
  });

  const segments = transcriptQuery.data?.segments ?? [];

  // Browser-side upload diagnostics — an operator tool, so ADMIN only.
  const clientLogQuery = useQuery({
    queryKey: ['client-log', id],
    queryFn: () => meetingsApi.clientLog(id),
    enabled: Boolean(id) && isAdmin,
  });

  // The audio src must NOT follow the query result: every refetch (the 8 s
  // processing poll, a segment edit, speaker naming) mints a new presigned URL,
  // and a changed src makes the browser reload the element and stop playback.
  // Pin the first URL we get; refreshAudio() replaces it only when it expires.
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const latestAudioUrl = transcriptQuery.data?.audioUrl ?? null;
  useEffect(() => {
    if (latestAudioUrl && !audioSrc) setAudioSrc(latestAudioUrl);
  }, [latestAudioUrl, audioSrc]);

  // "Speaker 10" must not sort before "Speaker 2".
  const speakers = useMemo(
    () =>
      [...new Set(segments.map((s) => s.speaker))].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
      ),
    [segments],
  );

  const saveSegment = useMutation({
    mutationFn: (vars: { segId: string; text: string }) =>
      meetingsApi.updateSegment(id, vars.segId, { text: vars.text }),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['transcript', id] });
    },
  });

  const mapSpeakers = useMutation({
    mutationFn: (mappings: { from: string; to: string }[]) => meetingsApi.mapSpeakers(id, mappings),
    onSuccess: () => {
      setSpeakerDialog(false);
      void queryClient.invalidateQueries({ queryKey: ['transcript', id] });
    },
  });

  const retranscribe = useMutation({
    mutationFn: () => meetingsApi.retranscribe(id),
    onSuccess: () => {
      setNotice(t('meeting.retranscribing'));
      void queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      void queryClient.invalidateQueries({ queryKey: ['transcript', id] });
    },
  });

  const seek = (segment: TranscriptSegment): void => {
    setActiveSegment(segment.id);
    const audio = audioRef.current;
    if (!audio) return;
    // Setting currentTime before metadata has loaded is silently dropped.
    if (audio.readyState < 1) {
      const onLoaded = (): void => {
        audio.currentTime = segment.startSec;
        void audio.play().catch(() => undefined);
      };
      audio.addEventListener('loadedmetadata', onLoaded, { once: true });
      audio.load();
      return;
    }
    audio.currentTime = segment.startSec;
    void audio.play().catch(() => undefined);
  };

  /**
   * Presigned audio URLs last five minutes. When one expires mid-review the
   * element errors; refetch the transcript for a fresh URL and carry on from
   * the same position.
   */
  const refreshAudio = async (): Promise<void> => {
    const audio = audioRef.current;
    const resumeAt = audio?.currentTime ?? 0;
    const wasPlaying = audio ? !audio.paused : false;
    if (refreshingAudio.current) return;
    refreshingAudio.current = true;
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ['transcript', id],
        queryFn: () => meetingsApi.transcript(id),
      });
      if (!fresh.audioUrl || !audioRef.current) return;
      const el = audioRef.current;
      setAudioSrc(fresh.audioUrl);
      el.src = fresh.audioUrl;
      const onLoaded = (): void => {
        el.currentTime = resumeAt;
        if (wasPlaying) void el.play().catch(() => undefined);
      };
      el.addEventListener('loadedmetadata', onLoaded, { once: true });
      el.load();
    } finally {
      refreshingAudio.current = false;
    }
  };

  if (meetingQuery.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (!meeting) return <div className="banner error">{t('common.error')}</div>;

  return (
    <>
      <div className="spread">
        <h1 style={{ marginBottom: 0 }}>{meeting.title}</h1>
        <StatusChip status={meeting.status} />
      </div>
      <p className="muted">
        {t(`types.${meeting.type}`)}
        {meeting.department?.name ? ` · ${meeting.department.name}` : ''}
        {meeting.clientRef ? ` · ${meeting.clientRef}` : ''}
        {meeting.durationSec ? ` · ${formatTimestamp(meeting.durationSec)}` : ''}
      </p>

      {notice && <div className="banner">{notice}</div>}
      {processing && <div className="banner">{t('meeting.processing')}</div>}
      {meeting.status === 'FAILED' && (
        <div className="banner error">
          <strong>{t('meeting.failed')}</strong>
          <div className="muted">{meeting.failureDetail}</div>
        </div>
      )}

      <div className="spread" style={{ marginBottom: 10 }}>
        <h2 style={{ marginBottom: 0 }}>{t('meeting.transcript')}</h2>
        <div className="row">
          {canWrite && segments.length > 0 && (
            <button type="button" className="secondary" onClick={() => setSpeakerDialog(true)}>
              {t('meeting.mapSpeakers')}
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className="secondary"
              disabled={retranscribe.isPending}
              onClick={() => retranscribe.mutate()}
            >
              {t('meeting.retranscribe')}
            </button>
          )}
        </div>
      </div>

      {transcriptQuery.data?.transcript && (
        <p className="muted">
          {t('meeting.provider')}: {transcriptQuery.data.transcript.provider} /{' '}
          {transcriptQuery.data.transcript.model} · v{transcriptQuery.data.transcript.version}
          {transcriptQuery.data.transcript.costUsd !== null
            ? ` · ${t('meeting.cost')}: $${transcriptQuery.data.transcript.costUsd.toFixed(3)}`
            : ''}
        </p>
      )}

      {segments.length === 0 && !processing && (
        <p className="muted">{t('meeting.noTranscript')}</p>
      )}

      <div className="card" style={{ padding: 4 }}>
        {segments.map((segment) => (
          <div
            key={segment.id}
            className={`segment ${activeSegment === segment.id ? 'active' : ''}`}
            onClick={() => seek(segment)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') seek(segment);
            }}
          >
            <div className="time" dir="ltr">
              {formatTimestamp(segment.startSec)}
            </div>
            <div>
              <div className="speaker">{segment.speaker}</div>
              {editing === segment.id ? (
                <div onClick={(e) => e.stopPropagation()} role="presentation">
                  <textarea
                    dir="auto"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <div className="row" style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      className="gold"
                      disabled={saveSegment.isPending}
                      onClick={() => saveSegment.mutate({ segId: segment.id, text: draft })}
                    >
                      {t('common.save')}
                    </button>
                    <button type="button" className="secondary" onClick={() => setEditing(null)}>
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  dir="auto"
                  onDoubleClick={() => {
                    if (!canWrite) return;
                    setEditing(segment.id);
                    setDraft(segment.text);
                  }}
                >
                  {segment.text}
                  {canWrite && (
                    <button
                      type="button"
                      className="link"
                      style={{ marginInlineStart: 8 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(segment.id);
                        setDraft(segment.text);
                      }}
                    >
                      {t('meeting.edit')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="audio-bar">
        {audioSrc ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            ref={audioRef}
            controls
            preload="metadata"
            src={audioSrc}
            onError={() => void refreshAudio()}
          />
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {t('meeting.audioMissing')}
          </p>
        )}
      </div>

      {isAdmin && (
        <details className="card">
          <summary>{t('meeting.diagnostics')}</summary>
          <ClientLogTable rows={clientLogQuery.data?.items ?? []} />
        </details>
      )}

      {speakerDialog && (
        <SpeakerDialog
          speakers={speakers}
          busy={mapSpeakers.isPending}
          onCancel={() => setSpeakerDialog(false)}
          onSave={(mappings) => mapSpeakers.mutate(mappings)}
        />
      )}
    </>
  );
}

function ClientLogTable({ rows }: { rows: ClientLogRow[] }): JSX.Element {
  const { t } = useTranslation();
  if (rows.length === 0) return <p className="muted">{t('meeting.diagnosticsEmpty')}</p>;
  return (
    <table dir="ltr">
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="muted" style={{ whiteSpace: 'nowrap' }}>
              {new Date(row.createdAt).toLocaleTimeString()}
            </td>
            <td style={{ color: row.level === 'info' ? undefined : 'var(--danger)' }}>
              {row.level}
            </td>
            <td>{row.event}</td>
            <td className="muted" style={{ wordBreak: 'break-all' }}>
              {row.detail ? JSON.stringify(row.detail) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SpeakerDialog({
  speakers,
  busy,
  onCancel,
  onSave,
}: {
  speakers: string[];
  busy: boolean;
  onCancel: () => void;
  onSave: (mappings: { from: string; to: string }[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [names, setNames] = useState<Record<string, string>>({});

  const save = (): void => {
    const mappings = speakers
      .map((s) => ({ from: s, to: (names[s] ?? '').trim() }))
      .filter((m) => m.to && m.to !== m.from);
    if (mappings.length > 0) onSave(mappings);
    else onCancel();
  };

  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div className="dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h2>{t('meeting.speakerDialog')}</h2>
        {speakers.map((s) => (
          <div className="field" key={s}>
            <label htmlFor={`spk-${s}`}>{s}</label>
            <input
              id={`spk-${s}`}
              value={names[s] ?? ''}
              placeholder={s}
              onChange={(e) => setNames((n) => ({ ...n, [s]: e.target.value }))}
            />
          </div>
        ))}
        <div className="row">
          <button type="button" className="gold" disabled={busy} onClick={save}>
            {t('common.save')}
          </button>
          <button type="button" className="secondary" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
